import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';

export const CallbackHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your verification...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const hash = location.hash;

        // Check for Cognito email verification parameters
        const confirmationCode = urlParams.get('confirmation_code');
        const username = urlParams.get('username') || urlParams.get('user_name');
        const clientId = urlParams.get('client_id');
        
        // Check for various other callback parameters
        const isEmailVerified = urlParams.get('email_verified') === 'true';
        const confirmationStatus = urlParams.get('confirmation_status') === 'SUCCESS';
        const hasAccessToken = hash.includes('access_token');
        const hasIdToken = hash.includes('id_token');
        const isCallbackPath = location.pathname === '/callback' || location.pathname === '/auth/callback';

        // Handle Cognito email verification callback
        if (confirmationCode && username && clientId) {
          setStatus('success');
          setMessage('Your email has been verified successfully! You can now sign in to your account.');

          // Redirect to sign in with success message after showing confirmation
          setTimeout(() => {
            navigate('/auth/signin?success=confirmed', { replace: true });
          }, 3000);
          return;
        }

        // Check if this is any other kind of successful verification/authentication callback
        if (isEmailVerified || confirmationStatus || hasAccessToken || hasIdToken || isCallbackPath) {
          setStatus('success');
          setMessage('Your email has been verified successfully! Redirecting you to the application...');

          // If we have tokens in the URL hash, the user is already authenticated
          if (hasAccessToken || hasIdToken || isAuthenticated) {
            // Wait a moment to show the success message, then redirect to chat
            setTimeout(() => {
              navigate('/chat', { replace: true });
            }, 2000);
          } else {
            // User needs to sign in, redirect to sign in with success message
            setTimeout(() => {
              navigate('/auth/signin?success=confirmed', { replace: true });
            }, 2000);
          }
        } else {
          // Check if there's an error parameter
          const error = urlParams.get('error');
          const errorDescription = urlParams.get('error_description');

          if (error) {
            setStatus('error');
            setMessage(errorDescription || 'There was an issue with verification. Please try signing in.');

            setTimeout(() => {
              navigate('/auth/signin', { replace: true });
            }, 3000);
          } else {
            // No specific parameters found, assume successful verification and redirect
            setStatus('success');
            setMessage('Verification complete! Redirecting you to sign in...');

            setTimeout(() => {
              navigate('/auth/signin?success=confirmed', { replace: true });
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Callback handling error:', error);
        setStatus('error');
        setMessage('There was an issue processing your verification. Please try signing in.');

        // Redirect to sign in after showing error
        setTimeout(() => {
          navigate('/auth/signin', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [location, navigate, isAuthenticated]);

  return (
    <div className="callback-handler">
      <div className="callback-content">
        <div className="callback-icon">
          {status === 'processing' && (
            <div className="loading-spinner" aria-label="Processing verification"></div>
          )}
          {status === 'success' && (
            <div className="success-icon" aria-label="Verification successful">✓</div>
          )}
          {status === 'error' && (
            <div className="error-icon" aria-label="Verification error">⚠</div>
          )}
        </div>

        <h1 className="callback-title">
          {status === 'processing' && 'Verifying Your Email'}
          {status === 'success' && 'Email Verified!'}
          {status === 'error' && 'Verification Issue'}
        </h1>

        <p className="callback-message">{message}</p>

        {status === 'processing' && (
          <div className="callback-progress">
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};